import Big from 'big.js';
import { useTranslations } from 'next-intl';
import { TypesInstruments, TypesSymbols, TypesOrderUnitType, calculator, format } from '@blofin/blofin-utils';
const { toConvertNumber } = calculator;
const { VaildNumber } = format;

interface Base {
  quantity: string;
  symbol: string;
  mark_price: string;
}

interface BaseAmount extends Base {
  filled_quantity: string;
  filled_amount: string;
  amount: string;
  quantity: string;
}

interface UseUnitParams {
  instruments: Record<string, TypesInstruments>;
  symbolsKeyby: Record<string, TypesSymbols>;
  orderUnitType: TypesOrderUnitType;
}

const useUnit = ({ instruments, symbolsKeyby, orderUnitType }: UseUnitParams) => {
  const t = useTranslations('trade');

  const getUnit = (base: string) => {
    if (orderUnitType === '0') {
      return base;
    } else if (orderUnitType === '1') {
      return t('cont');
    } else {
      return 'USDT';
    }
  };

  const getTotal = <T extends Base>(positionItem: T) => {
    try {
      let total = new Big(positionItem.quantity).times(symbolsKeyby[positionItem.symbol].contract_value).toString();
      if (orderUnitType === '0') {
        total = total;
      } else if (orderUnitType === '1') {
        total = positionItem.quantity;
      } else {
        const value = new Big(total)
          .times(positionItem.mark_price || instruments[positionItem.symbol]?.mark_price || 0)
          .toString();
        total = toConvertNumber(value, {
          precision: 2,
          radixPoint: 'SUPPLY_ZERO',
        });
      }
      return total;
    } catch (error) {
      console.log(error);
    }
    return '0';
  };

  const getUnitValue = (quantity: string, symbol: string, price?: string) => {
    try {
      let total = new Big(quantity).times(symbolsKeyby[symbol].contract_value).toString();
      if (orderUnitType === '0') {
        total = total;
      } else if (orderUnitType === '1') {
        total = toConvertNumber(quantity, {
          precision: 0,
        });
      } else {
        const value = new Big(total).times(price ? price : instruments[symbol]?.mark_price || 0).toString();
        total = toConvertNumber(value, {
          precision: 2,
          radixPoint: 'SUPPLY_ZERO',
        });
      }
      return total;
    } catch (error) {
      console.log(error);
    }
    return '0';
  };

  const transformUnit = <T extends Base>(positionItem: T, precision: number) => {
    if (orderUnitType === '0') {
      return formatValue(getTotal<T>(positionItem), precision);
    } else {
      return getTotal(positionItem);
    }
  };

  const formatValue = (val: number | string, precision = 2) => {
    return toConvertNumber(String(val), { precision: precision, radixPoint: 'SUPPLY_ZERO' });
  };

  const getQuantity = (quantity: string, percentage: number) => {
    const value = quantity === '' ? '0' : quantity;

    return new Big(value).times(percentage).div(100).toString();
  };

  const getAmountForBaseCurrency = (item: { filled_quantity: string; quantity: string; symbol: string }) => {
    let filled_quantity = '0';
    let quantity = '0';
    filled_quantity = new Big(VaildNumber(item.filled_quantity))
      .times(VaildNumber(symbolsKeyby[item.symbol].contract_value))
      .toString();
    quantity = new Big(VaildNumber(item.quantity))
      .times(VaildNumber(symbolsKeyby[item.symbol].contract_value))
      .toString();
    return {
      filled_quantity: toConvertNumber(filled_quantity, {
        precision: Number(symbolsKeyby[item.symbol].contract_digit),
        round: true,
      }),
      quantity: toConvertNumber(quantity, {
        precision: Number(symbolsKeyby[item.symbol].contract_digit),
        round: true,
      }),
    };
  };

  const getAmount = <T extends BaseAmount>(item: T) => {
    let filled_quantity = '0';
    let quantity = '0';
    if (orderUnitType === '0') {
      filled_quantity = new Big(VaildNumber(item.filled_quantity))
        .times(VaildNumber(symbolsKeyby[item.symbol].contract_value))
        .toString();
      quantity = new Big(VaildNumber(item.quantity))
        .times(VaildNumber(symbolsKeyby[item.symbol].contract_value))
        .toString();
      return {
        filled_quantity: toConvertNumber(filled_quantity, {
          precision: Number(symbolsKeyby[item.symbol].contract_digit),
          round: true,
        }),
        quantity: toConvertNumber(quantity, {
          precision: Number(symbolsKeyby[item.symbol].contract_digit),
          round: true,
        }),
      };
    } else if (orderUnitType === '1') {
      return {
        filled_quantity: item.filled_quantity,
        quantity: item.quantity,
      };
    } else {
      filled_quantity = item.filled_amount;
      quantity = item.amount;
      return {
        filled_quantity: toConvertNumber(filled_quantity, {
          precision: 2,
        }),
        quantity: toConvertNumber(quantity, {
          precision: 2,
        }),
      };
    }
  };

  return {
    getUnit,
    transformUnit,
    getUnitValue,
    getQuantity,
    getAmount,
    getAmountForBaseCurrency,
  };
};

export default useUnit;
